import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

/**
 * Shape of `GET /api/status/dashboard` — kept in sync with
 * `server/src/services/health-dashboard.ts` `buildDashboardViewModel`.
 *
 * Inlined here (rather than imported) because the Angular app and the
 * server share no TypeScript compilation unit. Adding fields server-side
 * is non-breaking; consumers just see `undefined` until they're rebuilt.
 */
export interface ServerStatusData {
  overall: 'ok' | 'degraded';
  overallReasons: string[];
  instance: {
    title: string;
    namespace?: string;
    /** Full commit SHA the server was built from, or null when unavailable. */
    version?: string | null;
    /** Where the SHA was sourced from — `env` (CI) or `git` (rev-parse fallback). */
    versionSource?: 'env' | 'git' | null;
    uptime: string;
    pid: number;
    nodeVersion: string;
  };
  resources: {
    eventLoopP50Ms: number;
    eventLoopP99Ms: number;
    eventLoopMaxMs: number;
    heapUsedMb: number;
    heapTotalMb: number;
    heapLimitMb: number;
    heapUsedPct: string;
    rssMb: number;
    externalMb: number;
    cpuUserMs: number;
    cpuSystemMs: number;
    gcPauseTotalMs: number;
    gcCount: number;
  };
  queue: {
    concurrency: number;
    maxAttempts: number;
    active: number;
    queued: number;
  };
  environment: {
    pandoc: {
      available: boolean;
      version: string | null;
      features: string | null;
      error: string | null;
    };
    templates: {
      configured: boolean;
      mounted: boolean;
      path: string | null;
      templateCount: number;
      error?: string;
    };
  };
  reachability: ReachabilityProbe[];
  stats: {
    last1h: StatsWindow;
    last24h: StatsWindow;
    totalInBuffer: number;
  };
  generatedAt: string;
}

export interface ReachabilityProbe {
  id: string;
  label: string;
  url: string;
  kind: 'http' | 'filesystem';
  /** Short, human-friendly line to show (domain for HTTP, path for fs). */
  displayLine: string;
  ok: boolean;
  status?: number; // HTTP status if a response came back — kept for debugging only, not surfaced
  latencyMs?: number;
  /** For filesystem probes: bytes free on the device backing the storage path. */
  freeBytes?: number;
  error?: string;
  lastCheckedAt: string;
}

export interface StatsWindow {
  windowLabel: string;
  total: number;
  imports: number;
  aiAnalyses: number;
  succeeded: number;
  failed: number;
  errorRate: number;
  retryableFailures: number;
  avgRunSeconds: number;
  avgQueueWaitSeconds: number;
  topFailureStages: { stage: string; count: number }[];
  byStageP50Ms: { stage: string; p50: number; p95: number; count: number }[];
}

/**
 * Singleton service that polls `/api/status/dashboard` and exposes the result
 * as Angular signals.
 *
 * Polling is reference-counted: it starts on the first `subscribe()` call and
 * stops after the matching `unsubscribe()`. This lets multiple components
 * (e.g. the /status page and a toolbar indicator) share one HTTP cadence
 * without duplicating requests.
 *
 * Use `refresh()` for a one-shot fetch without enabling polling.
 */
@Injectable({ providedIn: 'root' })
export class ServerStatusService {
  private readonly http = inject(HttpClient);
  private readonly DEFAULT_POLL_MS = 5_000;

  private readonly _data = signal<ServerStatusData | null>(null);
  private readonly _error = signal<string | null>(null);
  private readonly _loading = signal<boolean>(false);
  private readonly _consecutiveErrors = signal<number>(0);
  private readonly _firstAttemptAt = signal<number | null>(null);
  /**
   * Sticky flag for 401 responses. We stop polling once we see one (the
   * browser won't auto-attach Basic auth credentials to fetch/HttpClient
   * calls from a SPA path the same way it does for normal page loads, so
   * a 401 here usually means STATUS_PASSWORD is set but no credentials
   * were ever entered, or they were revoked). Polling resumes if the
   * user reloads the page after authenticating.
   */
  private readonly _authRequired = signal<boolean>(false);
  /**
   * How long we tolerate fetch failures before showing the user a real error.
   * During this window the UI shows "server starting up" instead of red.
   * Polling continues throughout — this is purely a presentation hint.
   */
  private readonly GRACE_PERIOD_MS = 15_000;
  private subscriberCount = 0;
  private pollHandle?: ReturnType<typeof setInterval>;

  /** Most recent successful payload, or null before the first fetch. */
  readonly data = this._data.asReadonly();
  /** Last fetch error, or null on success. */
  readonly error = this._error.asReadonly();
  readonly loading = this._loading.asReadonly();
  /**
   * True once the server returned 401 (STATUS_PASSWORD is set and the
   * client has no usable credentials). Components should surface a
   * persistent "sign in to view live status" banner — without it the
   * page would silently render stale or never-loaded data while the
   * background polling kept hitting the same 401.
   */
  readonly authRequired = this._authRequired.asReadonly();

  /**
   * "Starting up" = we've been failing for less than `GRACE_PERIOD_MS` and
   * have no data yet. Distinguishes the boot-time gap (Express not up, or
   * dev-proxy can't connect) from a definitive failure.
   */
  readonly isStartingUp = computed(() => {
    if (this._data() !== null) return false;
    if (this._consecutiveErrors() === 0) return true; // still trying the first request
    const first = this._firstAttemptAt();
    if (first == null) return true;
    return Date.now() - first < this.GRACE_PERIOD_MS;
  });

  // Convenience computed signals — useful for components that only need one slice.
  readonly overall = computed(() => this._data()?.overall ?? 'unknown');
  readonly instanceTitle = computed(() => this._data()?.instance.title ?? '');
  readonly queueActive = computed(() => this._data()?.queue.active ?? 0);
  readonly queueQueued = computed(() => this._data()?.queue.queued ?? 0);
  readonly degradedReasons = computed(() => this._data()?.overallReasons ?? []);

  /** Start (or join) the shared polling loop. Idempotent per caller — pair with `unsubscribe()`. */
  subscribe(intervalMs: number = this.DEFAULT_POLL_MS): void {
    this.subscriberCount += 1;
    if (this.subscriberCount === 1) {
      void this.refresh();
      this.pollHandle = setInterval(() => void this.refresh(), intervalMs);
    }
  }

  /** Release a subscription. The poller stops when the last subscriber leaves. */
  unsubscribe(): void {
    this.subscriberCount = Math.max(0, this.subscriberCount - 1);
    if (this.subscriberCount === 0 && this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
  }

  /** One-shot fetch. Does not enable or interact with the polling cadence. */
  async refresh(): Promise<void> {
    // Once we've seen a 401, every further poll would just repeat the same
    // 401. Skip the request entirely until the page reloads — the banner
    // surfaced by `authRequired` tells the user what to do.
    if (this._authRequired()) {
      return;
    }
    if (this._firstAttemptAt() == null) {
      this._firstAttemptAt.set(Date.now());
    }
    this._loading.set(true);
    try {
      // 503 is expected when the server self-reports as degraded; the body is
      // still the full view-model. We need `observe: 'response'` so we can read
      // the body on non-2xx responses without throwing.
      const response = await firstValueFrom(
        this.http.get<ServerStatusData>('/api/status/dashboard', {
          observe: 'response',
          // Tolerate any status >= 200 so a 503 doesn't trip the error branch —
          // Angular's HttpClient throws on non-2xx unless we widen the predicate.
          // We do that by inspecting status manually below.
        }),
      );
      const body = response.body;
      if (body && typeof body === 'object' && 'overall' in body) {
        this._data.set(body as ServerStatusData);
        this._error.set(null);
        this._consecutiveErrors.set(0);
      } else {
        // Unexpected body shape — treat as error.
        this._consecutiveErrors.update((n) => n + 1);
        this._error.set('Unexpected response from server');
      }
    } catch (e: any) {
      this._consecutiveErrors.update((n) => n + 1);
      // 401 means STATUS_PASSWORD is set and the request had no usable
      // credentials. Flip the sticky flag — every subsequent poll would
      // hit the same 401 and add nothing. Components watching
      // `authRequired` show a "sign in" banner; the user resolves it by
      // reloading the page after authenticating to the realm.
      if (e?.status === 401) {
        this._authRequired.set(true);
        this._error.set(this.describeError(e));
        // Tear down the polling timer now — the previous version left the
        // setInterval running, with each tick bailing at the top of
        // refresh(). Free the timer outright so the browser isn't kept
        // ticking every 5s for the page lifetime.
        if (this.pollHandle) {
          clearInterval(this.pollHandle);
          this.pollHandle = undefined;
        }
        return;
      }
      // For HTTP errors with a body that still matches our shape (server
      // returned 503 with the view-model), surface the body so the UI renders.
      const body = e?.error;
      if (body && typeof body === 'object' && 'overall' in body && 'resources' in body) {
        this._data.set(body as ServerStatusData);
        this._error.set(null);
      } else {
        this._error.set(this.describeError(e));
      }
    } finally {
      this._loading.set(false);
    }
  }

  private describeError(e: any): string {
    if (!e) return 'Could not load server status';
    if (e.status === 0) return 'Server unreachable (network error)';
    if (e.status === 502 || e.status === 503 || e.status === 504) {
      return 'Server is starting up or temporarily unavailable';
    }
    if (e.status === 401) return 'Authentication required';
    if (e.status === 500) return 'Server error — likely still booting';
    return e?.message ?? 'Could not load server status';
  }
}
